import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatPseudoCheckboxState,
  MatOption,
  MatOptgroup,
  MatPseudoCheckbox,
} from '@angular/material/core';
import { FormlyFieldSelect } from '@ngx-formly/material/select';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { FormlyModule } from '@ngx-formly/core';
import { NgFor, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormlyUserSelectOptionsPipe } from './formly-user-select-options.pipe';

@Component({
  selector: 'app-formly-user-select',
  template: `
    <ng-template #selectAll let-selectOptions="selectOptions">
      <mat-option (click)="toggleSelectAll(selectOptions)">
        <mat-pseudo-checkbox
          class="mat-option-pseudo-checkbox"
          [state]="_getSelectAllState(selectOptions)"
        >
        </mat-pseudo-checkbox>
        {{ props.selectAllOption }}
      </mat-option>
    </ng-template>
    <mat-select
      panelClass="user-select-panel"
      [id]="id"
      [formControl]="frmControl"
      [formlyAttributes]="field"
      [placeholder]="props.placeholder ?? ''"
      [tabIndex]="props.tabindex"
      [required]="required"
      [compareWith]="props.compareWith ?? defaultOptions.props.compareWith"
      [multiple]="props.multiple"
      (selectionChange)="change($event)"
      [errorStateMatcher]="errorStateMatcher"
      [disableOptionCentering]="props.disableOptionCentering"
      [typeaheadDebounceInterval]="props.typeaheadDebounceInterval"
    >
      <mat-select-trigger>
        <ng-container
          *ngFor="
            let opt of props.options
              | formlyUserSelectOptions: field
              | async as selectOptions
          "
        >
          @if (frmControlValueIsArray) {
            @if (formControl.value.includes(opt.value)) {
              {{ opt.label }}
              @if (opt.data?.userId) {
                <span class=""> &lt;{{ opt.data?.userId }}&gt;</span>
              }
              @if (!isLastElement(opt.value)) {
                <span> , </span>
              }
            }
          }
          @if (!frmControlValueIsArray) {
            @if (formControl.value === opt.value) {
              {{ opt.label }}
              @if (opt.data?.userId) {
                <span class=""> &lt;{{ opt.data?.userId }}&gt;</span>
              }
            }
          }
        </ng-container>
      </mat-select-trigger>

      @if (
        props.options | formlyUserSelectOptions: field | async;
        as selectOptions
      ) {
        @if (props.multiple && props.selectAllOption) {
          <ng-container
            [ngTemplateOutlet]="selectAll"
            [ngTemplateOutletContext]="{
              selectOptions: selectOptions,
            }"
          >
          </ng-container>
        }
        @for (item of selectOptions; track item) {
          @if (item.group) {
            <mat-optgroup [label]="item.label">
              @for (child of item.group; track child) {
                <mat-option [value]="child.value" [disabled]="child.disabled">
                  {{ child.label }}
                </mat-option>
              }
            </mat-optgroup>
          }
          @if (!item.group) {
            <mat-option [value]="item.value" [disabled]="item.disabled">
              {{ item.label }}
              @if (item.data?.userId) {
                <span class=""> &lt;{{ item.data?.userId }}&gt;</span>
              }
              @if (item.data?.occupation) {
                <span class="small"
                  ><br />{{ 'SHARED.USER_SELECT.OCCUPATION' | translate }}
                  {{ item.data?.occupation }}</span
                >
              }
            </mat-option>
          }
        }
      }
    </mat-select>
  `,
  styleUrls: ['./formly-user-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    FormlyModule,
    MatSelectTrigger,
    NgFor,
    NgTemplateOutlet,
    MatOptgroup,
    AsyncPipe,
    TranslateModule,
    FormlyUserSelectOptionsPipe,
    MatPseudoCheckbox,
  ],
})
export class FormlyUserSelectComponent extends FormlyFieldSelect {
  get frmControl(): FormControl {
    return this.formControl as FormControl;
  }

  get frmControlValueIsArray(): boolean {
    return this.frmControl.value instanceof Array;
  }

  isLastElement(value: any): boolean {
    if (this.frmControlValueIsArray) {
      return this.frmControl.value[this.frmControl.value.length - 1] === value;
    }

    return false;
  }

  _getSelectAllState(options: any): MatPseudoCheckboxState {
    const tmp: any = super.getSelectAllState(options);
    if (tmp === '') {
      return 'unchecked';
    } else {
      return tmp;
    }
  }
}
